import {
  faDrawCircle,
  faFolders,
  faGear,
  faHandsHoldingHeart,
  faHome,
  faRocket,
  faSidebar,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import type { JSX, ReactNode } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteMatch } from 'react-router-dom';

import { EOnboardingTaskState, EPackageTiers } from '@feathr/blackbox';
import {
  AvatarV2 as Avatar,
  Badge,
  Button,
  ListItem,
  Logo,
  Nav,
  NavItem,
  NavItemMinimizable,
  NavMenuProvider,
  Pill,
  Skeleton,
} from '@feathr/components';
import { TrackBlocked } from '@feathr/extender/components';
import { useAccountSwitcher } from '@feathr/extender/components/AccountSwitcher';
import AccountSwitcher from '@feathr/extender/components/AccountSwitcher';
import { EOnboardingProgressiveFeatures, useOnboardingStatus } from '@feathr/extender/hooks';
import {
  getContextualMessage,
  isTaskNotCompleted,
} from '@feathr/extender/hooks/useOnboardingStatus.utils';
import {
  useAccount,
  useFlags,
  useLocalUrl,
  useNavigation,
  useRole,
  useStore,
  useUser,
} from '@feathr/extender/state';
import { getEventIdFromUrl } from '@feathr/extender/utils';

import DataNav from './DataNav';
import EventNav from './EventNav';
import HomeNav from './HomeNav';
import SettingsNav from './SettingsNav';

import * as styles from './PrimaryNav.css';

interface IProps {
  className?: string;
  secondaryNav?: ReactNode;
}

function PrimaryNav({ className }: Readonly<IProps>): JSX.Element | null {
  // Get navigation state
  const {
    isMinimized,
    toggleMinimized,
    expandNavFromMinimized,
    setActiveSecondaryNav,
    scrollableRef,
    isTransitioningFromSecondary,
  } = useNavigation();
  const [isToggleHovered, setIsToggleHovered] = useState(false);

  // Get routing, account, and user info
  const localUrl = useLocalUrl();
  const { Events } = useStore();
  const eventId = getEventIdFromUrl();
  const selectedEvent = eventId ? Events.get(eventId) : undefined;
  const selectedEventLogo = selectedEvent?.get('logo');
  const match = useRouteMatch<{ accountId: string; primary: string }>({
    path: '/:accountId/:primary',
  });
  const account = useAccount();
  const user = useUser();
  const { isLoading: isProjectsPrereqLoading, task: projectsPrereqTask } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Projects,
    true,
  );
  const { isLoading: isProjectsLoading, task: projectsTask } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Projects,
  );
  const { isLoading: isHomePrereqLoading, task: homePrereqTask } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Home,
    true,
  );
  const { isLoading: isHomeLoading, task: homeTask } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Home,
  );
  const { isLoading: isCommunityLoading } = useOnboardingStatus(
    EOnboardingProgressiveFeatures.Community,
  );
  const { isMilestonesProgressLoading, milestonesProgress } = useOnboardingStatus();
  const { canSwitchAccounts, isLoading } = useAccountSwitcher({ accountId: account.id });
  const { t } = useTranslation();
  const { showOnboarding, enforceOnboarding, showFundraise } = useFlags();
  const isOnboardingActive = showOnboarding && enforceOnboarding;
  const { hasProjectsSome, hasSegmentsSome } = useRole();
  const hasProjectsOrSegments = hasProjectsSome || hasSegmentsSome;
  const isHomeLocked =
    (isOnboardingActive && isTaskNotCompleted(homeTask)) || hasProjectsOrSegments;
  const isProjectsLocked = isOnboardingActive && isTaskNotCompleted(projectsTask);

  const projectsPrereqCompleted =
    projectsPrereqTask?.get('state') === EOnboardingTaskState.Completed;
  const projectsTooltip = isProjectsLocked
    ? getContextualMessage(EOnboardingProgressiveFeatures.Projects, projectsPrereqCompleted, t)
    : undefined;

  const homePrereqCompleted = homePrereqTask?.get('state') === EOnboardingTaskState.Completed;
  const homeTooltip = isHomeLocked
    ? getContextualMessage(EOnboardingProgressiveFeatures.Home, homePrereqCompleted, t)
    : undefined;

  if (!match) {
    return null;
  }

  let primaryNavComponent: JSX.Element;
  if (
    isLoading ||
    isProjectsPrereqLoading ||
    isProjectsLoading ||
    isHomePrereqLoading ||
    isHomeLoading ||
    isCommunityLoading
  ) {
    primaryNavComponent = <Skeleton className={styles.skeleton} />;
  } else if (canSwitchAccounts) {
    primaryNavComponent = (
      <ListItem className={styles.accountSwitcher}>
        <AccountSwitcher />
      </ListItem>
    );
  } else if (account.isPlatformLite) {
    primaryNavComponent = (
      <>
        <Logo className={styles.logo} />
        <Pill theme={'cyan'}>Lite</Pill>
      </>
    );
  } else {
    primaryNavComponent = <Logo className={styles.logo} />;
  }

  const renderNavItems = (): JSX.Element => (
    <>
      {(account.hasActivePackage || user?.isSudoer) && (
        <>
          {showOnboarding && (
            <NavItem
              data-command={'true'}
              data-command-description={t('Get started with onboarding')}
              data-command-keywords={'quickstart,onboarding,getting started'}
              data-command-label={t('Quick Start')}
              icon={faRocket}
              suffix={
                !isMinimized ? (
                  <Link to={localUrl('/quickstart')}>
                    <Badge
                      size={'small'}
                      theme={'blue'}
                      value={
                        isMilestonesProgressLoading ? (
                          <Skeleton height={20} width={40} />
                        ) : (
                          `${milestonesProgress.completed} / ${milestonesProgress.total}`
                        )
                      }
                    />
                  </Link>
                ) : undefined
              }
              to={localUrl('/quickstart')}
              tooltip={isMinimized ? t('Quick Start') : undefined}
            >
              {!isMinimized && t('Quick Start')}
            </NavItem>
          )}
          <TrackBlocked
            featureName={'home'}
            locked={isHomeLocked}
            requiredPackage={isHomeLocked ? EPackageTiers.Starter : undefined}
          >
            <NavItemMinimizable
              data-command={'true'}
              data-command-description={t('Home dashboard and marketing')}
              data-command-keywords={'home,dashboard,marketing,overview'}
              data-command-label={t('Home')}
              icon={faHome}
              isActive={['accounts', 'dashboard', 'marketing'].includes(match.params.primary)}
              isMinimized={isMinimized}
              label={t('Home')}
              menu={isMinimized ? <HomeNav /> : undefined}
              onClick={!isMinimized ? (): void => setActiveSecondaryNav('home') : undefined}
              rel={'home'}
              to={localUrl('')}
              tooltip={homeTooltip}
            />
          </TrackBlocked>

          {showFundraise && (
            <TrackBlocked featureName={'fundraise'}>
              <NavItemMinimizable
                data-command={'true'}
                data-command-description={'Manage fundraising campaigns and donations'}
                data-command-keywords={'fundraising,donations,fundraise,giving,donate'}
                data-command-label={'Fundraising'}
                icon={faHandsHoldingHeart}
                isMinimized={isMinimized}
                label={t('Fundraising')}
                to={localUrl('/fundraise')}
              />
            </TrackBlocked>
          )}

          <TrackBlocked
            featureName={'projects'}
            locked={isProjectsLocked}
            requiredPackage={isProjectsLocked ? EPackageTiers.Starter : undefined}
          >
            <NavItemMinimizable
              data-command={'true'}
              data-command-description={t('Manage projects and campaigns')}
              data-command-keywords={'projects,campaigns,events'}
              data-command-label={t('Projects')}
              icon={faFolders}
              iconMinimized={
                selectedEventLogo ? (
                  <Avatar image={selectedEventLogo} name={selectedEvent?.name} size={'xs'} />
                ) : (
                  faFolders
                )
              }
              isMinimized={isMinimized}
              label={t('Projects')}
              menu={isMinimized ? <EventNav /> : undefined}
              onClick={!isMinimized ? (): void => setActiveSecondaryNav('projects') : undefined}
              to={localUrl('/projects')}
              tooltip={projectsTooltip ?? selectedEvent?.name}
            />
          </TrackBlocked>

          <NavItemMinimizable
            data-command={'true'}
            data-command-description={t('Community data and people management')}
            data-command-keywords={'community,data,people,segments,groups'}
            data-command-label={t('Community')}
            icon={faDrawCircle}
            isMinimized={isMinimized}
            label={t('Community')}
            menu={isMinimized ? <DataNav /> : undefined}
            onClick={!isMinimized ? (): void => setActiveSecondaryNav('community') : undefined}
            to={localUrl('/data')}
          />
        </>
      )}
      <NavItemMinimizable
        data-command={'true'}
        data-command-description={t('Manage account settings')}
        data-command-keywords={'settings,account,preferences'}
        data-command-label={t('Account Settings')}
        icon={faGear}
        isMinimized={isMinimized}
        label={t('Account Settings')}
        menu={isMinimized ? <SettingsNav /> : undefined}
        onClick={!isMinimized ? (): void => setActiveSecondaryNav('settings') : undefined}
        to={localUrl('/settings')}
      />
    </>
  );

  return (
    <NavMenuProvider>
      <Nav
        className={classNames(styles.root, className, { [styles.minimized]: isMinimized })}
        id={'primary-nav'}
        label={'Primary navigation'}
        type={'vertical'}
      >
        <span className={styles.logoContainer}>
          {!isMinimized && primaryNavComponent}
          <Button
            className={styles.toggleButton}
            onClick={isMinimized ? expandNavFromMinimized : toggleMinimized}
            onMouseEnter={(): void => setIsToggleHovered(true)}
            onMouseLeave={(): void => setIsToggleHovered(false)}
            title={isMinimized ? t('Expand navigation') : t('Collapse navigation')}
            type={'icon-ghost'}
          >
            <AnimatePresence initial={false} mode={'wait'}>
              {isMinimized && !isToggleHovered ? (
                <motion.span
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  key={'logo'}
                  transition={{ duration: 0.15 }}
                >
                  <Logo className={styles.feathrLogo} size={'small'} theme={'charcoal'} />
                </motion.span>
              ) : (
                <motion.span
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  key={'sidebar'}
                  transition={{ duration: 0.15 }}
                >
                  <FontAwesomeIcon icon={faSidebar} />
                </motion.span>
              )}
            </AnimatePresence>
          </Button>
        </span>

        <div
          className={styles.scrollableNav}
          data-name={'scrollable-nav'}
          ref={(node): void => {
            if (node) {
              scrollableRef.current = node;
            } else if (scrollableRef.current && !scrollableRef.current.isConnected) {
              scrollableRef.current = null;
            }
          }}
        >
          {isTransitioningFromSecondary ? (
            <motion.div
              animate={{ x: 0, opacity: 1 }}
              className={styles.transitionWrapper}
              initial={{ x: -75, opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {renderNavItems()}
            </motion.div>
          ) : (
            renderNavItems()
          )}
        </div>
      </Nav>
    </NavMenuProvider>
  );
}

export default observer(PrimaryNav);
